import React, { createRef } from "react";
import moment from "moment";
import fieldPropTypes from "./field-prop-types";
import { fieldValidation } from "../utils/field-validation";
import {
  DATE_FORMAT,
  DATE_ONLY_FORMAT,
  TIME_12H_FORMAT
} from "../utils/constants";
import { formDateValue, getMaxMinHours } from "../utils/hours-helper";

const GRID_STYLE = {
  display: "inline-grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  gridGap: "0 .5rem",
  marginBottom: "1rem"
};

class FormDateTimePicker extends React.Component {
  constructor(props) {
    super(props);
    this.wrapperRef = createRef();
    this.defaultBuffer = 60000 * 60 * 24 * 3;
    this.MHBuffer = 60000 * 60 * 24 * 2;
    this.defaultHour = 72;
    this.MHHour = 42;
    this.state = {
      date: null,
      time: null,
      displayDate: false,
      displayTime: false
    };
  }

  componentDidMount() {
    const {
      field: { name, params },
      appData: { values }
    } = this.props;
    const displayDate =
      params.includes("TDFieldOptionCalendarPicker") ||
      !params.includes("TDFieldOptionTimePicker");
    const displayTime =
      params.includes("TDFieldOptionTimePicker") ||
      !params.includes("TDFieldOptionCalendarPicker");
    const dateFormat = moment(
      values[name],
      "YYYY-MM-DD HH:mm:ss Z",
      true
    ).isValid()
      ? "YYYY-MM-DD HH:mm:ss Z"
      : DATE_ONLY_FORMAT;
    const value = values[name]
      ? moment(values[name], dateFormat).toDate()
      : null;
    this.setState({
      date: value,
      time: value,
      displayDate,
      displayTime
    });
  }

  componentDidUpdate() {
    const { field, appData } = this.props;
    fieldValidation[field.type].update(field, appData, {
      fieldRef: this.wrapperRef
    });
  }

  handleDateChange = date => {
    this.setState({ date }, this.handleChangeCallback);
  };

  handleTimeChange = time => {
    this.setState({ time }, this.handleChangeCallback);
  };

  handleChangeCallback() {
    const {
      field,
      appData: { handleChange }
    } = this.props;
    const { date, time, displayDate, displayTime } = this.state;
    let value;
    if (displayDate && displayTime) {
      if (date && time) {
        value = formDateValue(time, date);
      } else {
        value = moment(date).format(DATE_FORMAT);
      }
    } else if (date) {
      value = moment(date).format(DATE_FORMAT);
    } else if (time) {
      value = date ? moment(time).format(TIME_12H_FORMAT) : null;
    }

    handleChange({ [field.name]: value });
  }

  getDate() {
    const {
      field: { name },
      appData: { values }
    } = this.props;

    return values[name] ? moment(values[name], DATE_FORMAT).toDate() : null;
  }

  render() {
    const { field, components, appData } = this.props;
    const { Label, DatePicker, TimePicker } = components;
    const { action, name, placeholder = "", params } = field;
    const isVitalsDatepicker = params.includes("TDFieldOptionVitalsDatepicker");
    let delay, minHour, maxHour, startDate, endDate;

    if (action) {
      const { data } = action;
      delay = data?.delay || 0;
      minHour = data?.min_hour || 0;
      maxHour = data?.max_hour || 12;
      startDate = data?.start_date || null;
      endDate = data?.end_date ? new Date(data?.end_date) : null;
    }

    const { displayDate, displayTime } = this.state;

    // default buffer 72 hours
    let buffer = this.defaultBuffer;
    let hours = this.defaultHour;
    if (this.props.appData.values.health_category === "Mental Health") {
      // provide buffer for 48 hours in case of MH
      buffer = this.MHBuffer;
      hours = this.MHHour;
    }
    const delayForMinDate = delay ? delay * 1000 : buffer;

    const minDate = isVitalsDatepicker
      ? moment(startDate).add(delayForMinDate, "millisecond").toDate()
      : new Date(
          Date.now() +
            (params.includes("TDFieldOptionAdjustForTimezone")
              ? 0
              : delayForMinDate)
        );

    const adjustedMinDate = !moment(this.state.time)
      .add(hours, "hours")
      .isAfter(minDate)
      ? moment(minDate).add(1, "day").toDate()
      : minDate;

    const maxDate = moment(adjustedMinDate).isAfter(endDate) ? null : endDate;

    let datePicker;
    let timePicker;

    const isDatePickerInputFieldClearable = true;
    const { isPulse, isEnhancedRedesign } = appData;

    if (displayDate) {
      datePicker = (
        <DatePicker
          isClearable={isDatePickerInputFieldClearable}
          value={this.getDate()}
          minDate={adjustedMinDate}
          maxDate={maxDate}
          onChange={this.handleDateChange}
          // placeholderText={placeholder.replace("%@", "Date")}
          components={components}
          label={name}
          usePulseStyles={isPulse || isEnhancedRedesign}
          appData={appData}
        />
      );
    }
    if (displayTime) {
      const isMinDateSelected = moment(this.state.date).isSame(minDate, "day");
      const { minTime, maxTime } = getMaxMinHours(
        maxHour,
        minHour,
        isMinDateSelected
      );
      timePicker = (
        <TimePicker
          value={this.state.time}
          minTime={minTime}
          maxTime={maxTime}
          onChange={this.handleTimeChange}
          components={components}
          appData={appData}
        />
      );
    }

    const Wrapper = props => <div {...props} ref={this.wrapperRef} />;

    if (displayDate && displayTime) {
      return (
        <Wrapper style={GRID_STYLE}>
          <Label name={name} text={placeholder.replace("%@", "Time")} />
          {datePicker}
          {timePicker}
        </Wrapper>
      );
    }
    if (displayDate) {
      const labelText = field.placeholder?.replace("%@", "Time") || field.title;

      return (
        <Wrapper className='field-wrapper-web-sdk'>
          {labelText && <Label name={name} text={labelText} />}
          {datePicker}
        </Wrapper>
      );
    }
    if (displayTime) {
      return (
        <Wrapper className='field-wrapper-web-sdk'>
          <Label name={name} text={placeholder.replace("%@", "Time")} />
          {timePicker}
        </Wrapper>
      );
    }
    return null;
  }
}

FormDateTimePicker.propTypes = fieldPropTypes;

export default FormDateTimePicker;
